'use client'

import { ChatCircle, Lightbulb, RocketLaunch } from 'phosphor-react'
import { formatFontFamily } from '@/lib/themeHelpers'
import { useAssistant } from '@/lib/assistantContext'

interface CampaignStep {
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface CampaignSectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  iconWeight: string;
  content: {
    title: string;
    description: string;
    steps: CampaignStep[];
    ctaText: string;
  };
}

const iconMap: Record<string, React.ElementType> = {
  chat: ChatCircle,
  lightbulb: Lightbulb,
  rocket: RocketLaunch
};

export default function CampaignSection({
  surface,
  theme,
  radius,
  fontFamily,
  gradStart,
  gradEnd,
  angle,
  content
}: CampaignSectionProps) {
  const { openPanel } = useAssistant();
  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  return (
    <section className="relative isolate overflow-hidden" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        {/* Centered Header */}
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight font-primary" style={{ color: theme.text }}>
            {content.title}
          </h2>
          <p className="mt-4 text-lg font-secondary max-w-2xl mx-auto" style={{ color: theme.mutedText }}>
            {content.description}
          </p>
        </div>

        {/* Three Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {content.steps.map((step) => {
            const IconComponent = iconMap[step.icon] || ChatCircle;
            return (
              <div 
                key={step.number}
                className="relative flex flex-col items-center text-center p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e5e5',
                  borderRadius: 12
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                {/* Number Badge */}
                <div 
                  className="absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 flex items-center justify-center text-sm font-bold rounded-full"
                  style={{ 
                    backgroundColor: '#E8EDF5',
                    color: '#103065',
                    border: '2px solid #103065'
                  }}
                >
                  {step.number}
                </div>
                
                {/* Inline Icon + Title */}
                <div className="flex items-center gap-2 mt-6 mb-2">
                  <IconComponent size={20} weight="duotone" color="#103065" />
                  <h3 
                    className="text-xl font-bold font-primary"
                    style={{ color: '#1E1E1E' }}
                  >
                    {step.title}
                  </h3>
                </div>
                
                {/* Description */}
                <p 
                  className="text-sm font-secondary leading-relaxed"
                  style={{ color: '#6b7280' }}
                >
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Centered CTA Button */}
        <div className="flex justify-center">
          <button 
            className="px-12 py-4 text-base font-semibold shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105 font-primary min-w-[200px]"
            style={{
              backgroundColor: '#F5CC5C',
              color: '#103065',
              borderRadius: radius,
              border: 'none'
            }}
            onClick={openPanel}
          >
            {content.ctaText}
          </button>
        </div>
      </div>
    </section>
  );
}
